import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActiveCartService } from '@cart/data-access';
import { ReceiptTypeCodes, SelectedPaymentMode } from '@checkout-v2/const';
import { PaymentModesService, PickupService, ReceiptService, TimeSlotsService } from '@checkout-v2/feature';
import { PaymentService } from '@payment/feature';
import { SystemMessageComponent } from '@sdvor/shared/ui/v2';
import { CurrentProfileService } from '@user/feature';
import { CheckoutValidationErrorComponent } from '../checkout-validation-error';
import { PaymentModesListComponent } from './payment-modes-list';
import { PaymentScheduleComponent } from './payment-schedule';

@Component({
  selector: 'sd-payment-modes',
  templateUrl: './payment-modes.component.html',
  styleUrls: ['./payment-modes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PaymentModesListComponent,
    PaymentScheduleComponent,
    CheckoutValidationErrorComponent,
    SystemMessageComponent,
  ],
})
export class PaymentModesComponent implements OnInit {
  private readonly receiptService = inject(ReceiptService);
  private readonly timeSlotsService = inject(TimeSlotsService);
  private readonly pickupService = inject(PickupService);
  private readonly currentProfileService = inject(CurrentProfileService);
  private readonly paymentModesService = inject(PaymentModesService);
  private readonly activeCartService = inject(ActiveCartService);
  private readonly paymentService = inject(PaymentService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isLegalProfile = toSignal(this.currentProfileService.getLegalEntityStatus());
  private readonly activeReceiptTypeId = toSignal(this.receiptService.getActiveReceiptTypeId());

  private readonly isTimeSlotsEmptyOrHasError = toSignal(this.timeSlotsService.isEmptyOrHasError());
  private readonly isTimeSlotsValid = toSignal(this.timeSlotsService.isValid());

  private readonly isPickupShopsEmptyOrHasError = toSignal(this.pickupService.isEmptyOrHasError());
  private readonly isPickupShopValid = toSignal(this.pickupService.isValid());

  private readonly activeCart = toSignal(this.activeCartService.getActive());
  private readonly activeTimeSlot = toSignal(this.timeSlotsService.getSelectedTimeSlot());

  private readonly receiptTypeCodes = ReceiptTypeCodes;

  protected readonly showPaymentModes = computed(() => {
    const isLegalProfile = this.isLegalProfile();
    const activeReceiptTypeId = this.activeReceiptTypeId();
    const isTimeSlotsEmptyOrHasError = this.isTimeSlotsEmptyOrHasError();
    const isTimeSlotsValid = this.isTimeSlotsValid();
    const isPickupShopsEmptyOrHasError = this.isPickupShopsEmptyOrHasError();
    const isPickupShopValid = this.isPickupShopValid();

    if (isLegalProfile) {
      return false;
    }

    return activeReceiptTypeId === 'delivery'
      ? !isTimeSlotsEmptyOrHasError && isTimeSlotsValid
      : !isPickupShopsEmptyOrHasError && isPickupShopValid;
  });

  protected readonly isPrepaymentRequired = computed(() => {
    const activeCart = this.activeCart();

    return activeCart?.isInflow ?? false;
  });

  protected readonly canPayInplace = computed(() => {
    const activeReceiptTypeId = this.activeReceiptTypeId();
    const activeTimeSlot = this.activeTimeSlot();

    return (
      (activeReceiptTypeId === this.receiptTypeCodes.Delivery &&
        !!activeTimeSlot &&
        activeTimeSlot.price.canPayInplace) ??
      false
    );
  });

  protected readonly activePaymentMode = signal<SelectedPaymentMode | undefined>(undefined);

  protected readonly isPaymentModesLoading = toSignal(this.paymentModesService.getListLoading());
  protected readonly isError = toSignal(this.paymentModesService.getListError());
  protected readonly isSuccess = toSignal(this.paymentModesService.getListSuccess());
  protected readonly paymentModesList = toSignal(this.paymentModesService.getList(), { initialValue: [] });
  protected readonly bankCardsList = toSignal(this.paymentService.getPayments(), { initialValue: [] });
  protected readonly isBankCardsLoading = toSignal(this.paymentService.getPaymentsLoading());

  constructor() {
    let isInitialRun = true;

    effect(() => {
      const activePaymentMode = this.activePaymentMode();

      if (isInitialRun) {
        isInitialRun = false;

        return;
      }

      this.paymentModesService.setActivePaymentMode(activePaymentMode);
    });
  }

  ngOnInit(): void {
    // синхронизация значения со стором
    this.paymentModesService
      .getActivePaymentMode()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((activePaymentModeFromStore) => {
        if (
          activePaymentModeFromStore?.code !== this.activePaymentMode()?.code ||
          activePaymentModeFromStore?.bindingId !== this.activePaymentMode()?.bindingId
        ) {
          this.activePaymentMode.set(activePaymentModeFromStore);
        }
      });
  }

  onClickBindNewCard(): void {
    this.paymentModesService.initCardBindingOnExternalAddress();
  }
}
